import React, { FC, useState } from 'react';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import { Chip } from '@mui/material';
import { QueryObserverResult } from '@tanstack/react-query';
import cn from 'classnames';
import { OrderCreationModal } from 'components/admin/orders/recurring-orders/components/OrderCreationModal';
import styles from 'components/admin/orders/recurring-orders/individualRecurringOrder.module.scss';
import { Button } from 'components/common/buttons/Button';
import { TimelineItem as Item } from 'api/admin/orders/getRecurringOrder';
import { ORIGIN_DISPLAY } from 'constants/recurringOrders/origins';

export type UpcomingInstanceItemType = Omit<Item, 'instanceWeek' | 'data'> & {
  /**
   * ISO string
   */
  instanceWeek: string;
  data: {
    pickupWindow: string;
    returnWindow: string;
    isSkipped: boolean;
    isCustomInstance?: boolean;
    jobRunMoment: string;
    isoInstanceWeek: string;
  };
};

export const UpcomingInstanceItem: FC<{
  item: UpcomingInstanceItemType;
  recurringOrderId: number | string;
  isOrderCreationAvailable: boolean;
  refetchRecurringOrderDetails: () => Promise<QueryObserverResult>;
}> = ({ item, recurringOrderId, isOrderCreationAvailable, refetchRecurringOrderDetails }) => {
  const isInstanceSkipped = item.data.isSkipped;
  const isCustomInstance = item.data.isCustomInstance;
  const [isOrderCreationModalOpened, setIsOrderCreationModalOpened] = useState(false);
  const openOrderCreationModal = () => {
    setIsOrderCreationModalOpened(true);
  };
  const onCloseOrderCreationModal = () => {
    setIsOrderCreationModalOpened(false);
  };
  const isJobScheduled = item.data.jobRunMoment;

  return (
    <>
      <TimelineItem
        key={`${item.itemTimestamp} ${item.instanceWeek}`}
        className={styles.timeLineItem}
      >
        <TimelineOppositeContent color="textSecondary" className={styles.timestamp}>
          {isJobScheduled ? (
            <>
              {item.data.jobRunMoment} {!isInstanceSkipped && <span>(scheduled)</span>}{' '}
            </>
          ) : (
            <span className={styles.dangerText}>Order creation is not scheduled!</span>
          )}
          <p className={styles.target}>
            Pickup week: <span>{item.instanceWeek}</span>
          </p>
        </TimelineOppositeContent>
        <TimelineSeparator>
          <TimelineDot className={isInstanceSkipped ? styles.skippedItem : styles.scheduledItem} />
          <TimelineConnector className={styles.scheduledItem} />
        </TimelineSeparator>
        <TimelineContent className={styles.timelineItemContent}>
          <p
            className={cn(styles.title, {
              [styles.dangerText]: isInstanceSkipped,
            })}
          >
            {isInstanceSkipped ? 'Order skipped' : 'Order scheduled'}{' '}
            {isCustomInstance && !isInstanceSkipped && (
              <Chip label="custom" size="small" className={styles.chip} />
            )}
          </p>
          {isOrderCreationAvailable && (
            <Button onClick={openOrderCreationModal}>Create an order now</Button>
          )}
          <p className={styles.target}>
            Pickup window: <span>{item.data.pickupWindow}</span>
          </p>
          <p className={styles.target}>
            Return window: <span>{item.data.returnWindow}</span>
          </p>
          {item.teamMemberName && isInstanceSkipped && (
            <p className={styles.author}>
              Skipped by: <span>{item.teamMemberName}</span>
            </p>
          )}
          {item.origin && isInstanceSkipped && (
            <p className={styles.author}>
              Skip origin: <span>{ORIGIN_DISPLAY[item.origin]}</span>
            </p>
          )}
        </TimelineContent>
      </TimelineItem>
      {isOrderCreationModalOpened && (
        <OrderCreationModal
          item={item}
          recurringOrderId={recurringOrderId}
          onClose={onCloseOrderCreationModal}
          refetchRecurringOrderDetails={refetchRecurringOrderDetails}
        />
      )}
    </>
  );
};
